import { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { webChatService } from '../../../../services/webchat.service';
import { 
  WebSocketMessage, 
  ServerMessage, 
  AudioQueueItem, 
  SpeechRecognition, 
  SpeechRecognitionEvent, 
  SpeechRecognitionErrorEvent, 
  UseVoiceChatbotReturn, 
  ChatStatus 
} from '../types/voice-chatbot';
import { IInitialState, DataContext } from '../../../../models/chat.models';

const useVoiceChatbot = (): UseVoiceChatbotReturn => {
  // Obtener configuración desde el contexto de React
  const context: IInitialState = useContext(DataContext);
  
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isAISpeaking, setIsAISpeaking] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [streamingActive, setStreamingActive] = useState<boolean>(false);
  const [isPlayingStream, setIsPlayingStream] = useState<boolean>(false);
  const [bufferSize, setBufferSize] = useState<number>(2);
  const [consoleMessages, setConsoleMessages] = useState<string[]>([]);
  // Usar las configuraciones directamente del contexto
  const websocketUrl = context.webChatSettings.ChatBotApiUrl || null;
  
  const recognition = useRef<SpeechRecognition | null>(null);
  const audioQueue = useRef<AudioQueueItem[]>([]);
  const currentAudio = useRef<HTMLAudioElement | null>(null);
  const micStream = useRef<MediaStream | null>(null);
  const micReady = useRef<boolean>(false);
  const sessionId = useRef<string | null>(null);
  const lastAIText = useRef<string>('');
  const phraseCounter = useRef<number>(0);
  const aiSpeakingTimeout = useRef<NodeJS.Timeout | null>(null);
  const recognitionRestartTimeout = useRef<NodeJS.Timeout | null>(null);

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(websocketUrl, {
    shouldReconnect: () => !!websocketUrl,
    onOpen: () => {
      console.log('WebSocket connected - waiting for authentication challenge');
      addConsoleMessage('🔗 WebSocket connected - waiting for server challenge...');
    },
    onClose: (event: CloseEvent) => {
      switch (event.code) {
        case 4001:
          addConsoleMessage('❌ Authentication required');
          break;
        case 4002:
          addConsoleMessage('❌ Authentication timeout');
          break;
        case 4003:
          addConsoleMessage('❌ Invalid token - Please refresh');
          break;
        default:
          console.log('WebSocket disconnected');
      }
    }
  });

  // Stable version of addConsoleMessage to prevent unnecessary re-renders
  const addConsoleMessage = useCallback((message: string): void => {
    const timestamp = new Date().toLocaleTimeString();
    const newMessage = `[${timestamp}] ${message}`;
    console.log(newMessage);
    setConsoleMessages(prev => [...prev, newMessage]);
  }, []);

  // Update connection status based on WebSocket readyState
  useEffect(() => {
    setIsConnected(readyState === ReadyState.OPEN);
  }, [readyState]);

  const isLikelyEcho = useCallback((userText: string, aiText: string): boolean => {
    if (!aiText || !userText) return false;
    
    const userLower = userText.toLowerCase().trim();
    const aiLower = aiText.toLowerCase().trim();
    
    if (userLower === aiLower) return true;
    if (userLower.length > 8 && aiLower.includes(userLower)) return true;
    if (aiLower.length > 8 && userLower.includes(aiLower)) return true;
    if (userLower.length < 5 && aiLower.includes(userLower)) return true;
    
    const userWords = userLower.split(' ').filter(w => w.length > 2);
    const aiWords = aiLower.split(' ').filter(w => w.length > 2);
    
    if (userWords.length === 0) return false;
    if (aiWords.length === 0) return false;
    
    let matchingWords = 0;
    for (let word of userWords) {
      if (aiWords.includes(word)) {
        matchingWords++;
      }
    }
    
    return (matchingWords / userWords.length) > 0.4;
  }, []);

  // Función para detectar patrones sospechosos
  const isSuspiciousPattern = useCallback((text: string): boolean => {
    const lower = text.toLowerCase().trim();
    
    const suspiciousPatterns = [
      /^(um|uh|eh|ah|mm)\s*$/,
      /^[a-z]\s*$/,
      /^\d+\s*$/,
      /^(yes|yeah|no|ok)\s*$/,
      /^(the|and|or|but|in|on|at)\s*$/,
      /^[^\w\s]*$/,
      /(.)\1{3,}/
    ];
    
    return suspiciousPatterns.some(pattern => pattern.test(lower));
  }, []);

  // Función para verificar si debe descartar el texto
  const checkIfDiscarded = useCallback((transcript: string): boolean => {
    if (isAISpeaking) {
      addConsoleMessage(`🔇 Filtered (AI speaking): "${transcript}"`);
      return true;
    }
    
    if (isLikelyEcho(transcript, lastAIText.current)) {
      addConsoleMessage(`🔇 Echo filtered: "${transcript}"`);
      return true;
    }
    
    if (isSuspiciousPattern(transcript)) {
      addConsoleMessage(`🔇 Pattern filtered: "${transcript}"`);
      return true;
    }
    
    if (transcript.length < 3) {
      addConsoleMessage(`🔇 Too short: "${transcript}"`);
      return true;
    }

    return false;
  }, [isAISpeaking, isLikelyEcho, isSuspiciousPattern, addConsoleMessage]);

  // Asegurar permisos del micrófono
  const ensureMicPermission = useCallback(async (): Promise<void> => {
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (status.state === 'granted') { 
          micReady.current = true; 
          return; 
        }
      } catch (_) {}
    }

    if (!micStream.current) {
      micStream.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = ctx.createMediaStreamSource(micStream.current);
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(gain).connect(ctx.destination);
    }

    micReady.current = true;
  }, []);

  // Iniciar reconocimiento
  const startRecognition = useCallback((): void => {
    if (recognition.current) {
      try {
        recognition.current.start();
      } catch (error) {
        console.log('Recognition already active or error:', (error as Error).message);
      }
    }
  }, []);

  // Detener reconocimiento
  const stopRecognition = useCallback((): void => {
    if (recognition.current) {
      recognition.current.stop();
    }
  }, []);

  // Reproducir audio desde base64
  const playAudioFromBase64 = useCallback((base64Data: string, text: string): void => {
    try {
      if (currentAudio.current) {
        currentAudio.current.pause();
        currentAudio.current = null;
      }
      
      setIsAISpeaking(true);
      lastAIText.current = text;
      if (aiSpeakingTimeout.current) {
        clearTimeout(aiSpeakingTimeout.current);
      }
      
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      currentAudio.current = audio;
      
      audio.volume = 0.6;
      
      audio.onplay = () => {
        stopRecognition();
        addConsoleMessage(`🔊 Playing: "${text}"`);
      };
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudio.current = null;
        startRecognition();
        
        aiSpeakingTimeout.current = setTimeout(() => {
          setIsAISpeaking(false);
        }, 400);
      };
      
      audio.onerror = (error) => {
        console.error('❌ Error playing audio:', error);
        addConsoleMessage('❌ Error playing audio response');
        URL.revokeObjectURL(audioUrl);
        currentAudio.current = null;
        
        aiSpeakingTimeout.current = setTimeout(() => {
          setIsAISpeaking(false);
        }, 1000);
      };
      
      audio.play();
      
    } catch (error) {
      console.error('❌ Error processing audio:', error);
      addConsoleMessage('❌ Error processing audio response');

      aiSpeakingTimeout.current = setTimeout(() => {
        setIsAISpeaking(false);
      }, 1000);
    }
  }, [addConsoleMessage, startRecognition, stopRecognition]);

  // Reproducir siguiente audio streaming
  const playNextStreamingAudio = useCallback((): void => {
    if (audioQueue.current.length === 0) {
      setIsPlayingStream(false);
      
      setTimeout(() => {
        setIsAISpeaking(false);
        setStreamingActive(false);
        
        if (isListening) {
          startRecognition();
          addConsoleMessage('🎤 Streaming complete - microphone reactivated');
        }
      }, 400);
      
      return;
    }

    const audioItem = audioQueue.current.shift()!;
    setIsPlayingStream(true);
    setIsAISpeaking(true);
    
    stopRecognition();

    try {
      if (currentAudio.current) {
        currentAudio.current.pause();
        currentAudio.current = null;
      }

      const binaryString = atob(audioItem.audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      currentAudio.current = audio;
      
      audio.volume = 0.6;
      
      audio.onplay = () => {
        stopRecognition();
        addConsoleMessage(`🔊 Playing streaming: "${audioItem.text}"`);
      };
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudio.current = null;
        
        setTimeout(() => {
          playNextStreamingAudio();
        }, 200);
      };
      
      audio.onerror = (error) => {
        console.error('❌ Error playing streaming audio:', error);
        URL.revokeObjectURL(audioUrl);
        currentAudio.current = null;
        
        setTimeout(() => {
          playNextStreamingAudio();
        }, 500);
      };
      
      audio.play();
      
    } catch (error) {
      console.error('❌ Error processing streaming audio:', error);
      setTimeout(() => {
        playNextStreamingAudio();
      }, 500);
    }
  }, [isListening, addConsoleMessage, startRecognition, stopRecognition]);

  // Manejar audio streaming
  const handleStreamingAudio = useCallback((base64Data: string, text: string, isFinal: boolean): void => {
    try {
      if (!streamingActive) {
        stopRecognition();
        setIsAISpeaking(true);
        setStreamingActive(true);
      }
      
      audioQueue.current.push({
        audioData: base64Data,
        text: text,
        isFinal: isFinal
      });

      addConsoleMessage(`🎵 Audio chunk queued: "${text}" (${audioQueue.current.length} in buffer)`);
      
      if (!isPlayingStream && audioQueue.current.length >= bufferSize) {
        setIsPlayingStream(true);
        playNextStreamingAudio();
      } else if (!isPlayingStream && isFinal && audioQueue.current.length > 0) {
        setIsPlayingStream(true);
        playNextStreamingAudio();
      }
      
    } catch (error) {
      console.error('❌ Error handling streaming audio:', error);
      addConsoleMessage('❌ Error processing streaming audio');
    }
  }, [streamingActive, isPlayingStream, bufferSize, addConsoleMessage, stopRecognition, playNextStreamingAudio]);

  // Resetear estado streaming
  const resetStreamingState = useCallback((): void => {
    stopRecognition();
    
    audioQueue.current = [];
    setIsPlayingStream(false);
    setStreamingActive(false);
    
    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current = null;
    }
    
    setTimeout(() => {
      setIsAISpeaking(false);
      if (isListening) {
        startRecognition();
        addConsoleMessage('🔄 Streaming reset - microphone ready');
      }
    }, 500);
  }, [isListening, addConsoleMessage, startRecognition, stopRecognition]);

  // Enviar texto al servidor
  const sendTextToServer = useCallback((text: string): boolean => {
    if (!isConnected) {
      resetStreamingState();
      setStreamingActive(true);
      
      const message: ServerMessage = {
        type: 'speech',
        text: text,
        source: 'elastic_streaming',
        timestamp: new Date().toISOString(),
        branchId: context.webChatSettings.BranchId ? context.webChatSettings.BranchId.toString() : null,
        sessionId: sessionId.current!
      };
      
      sendJsonMessage(message);
      
      return true;
    } else {
      addConsoleMessage('❌ Server connection error');
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendJsonMessage, isConnected, resetStreamingState]);

  // Función para obtener un nuevo token cuando expire
  const refreshAuthToken = useCallback(async (): Promise<void> => {
    try {
      addConsoleMessage('🔄 Refreshing authentication token...');
      
      // Enviar el nuevo token al servidor
      const authMessage = {
        type: 'auth',
        token: context.webChatSettings.ChatBotApiAccessToken,
        timestamp: new Date().toISOString()
      };
      sendJsonMessage(authMessage);
      addConsoleMessage('🔐 New authentication token sent to server');
    } catch (error) {
      console.error('Error refreshing token:', error);
      addConsoleMessage('❌ Failed to refresh authentication token');
    }
  }, [sendJsonMessage, addConsoleMessage]);

  // Manejar mensajes del servidor
  const handleServerMessage = useCallback((data: WebSocketMessage): void => {
    console.log('Received server message:', data);
    switch (data.type) {
      case 'connection':
        addConsoleMessage(`✅ ${data.message}`);
        break;

      case 'authentication':
        // Servidor pide autenticación
        addConsoleMessage('🔐 Server requesting authentication...');
        if (context.webChatSettings.ChatBotApiAccessToken) {
          const authMessage = {
            type: 'auth',
            token: context.webChatSettings.ChatBotApiAccessToken,
            timestamp: new Date().toISOString()
          };
          sendJsonMessage(authMessage);
          addConsoleMessage('🔐 Authentication token sent to server');
        } else {
          addConsoleMessage('❌ No authentication token available');
        }
        break;

      case 'auth_failed':
        addConsoleMessage('❌ Authentication failed - invalid token');
        break;
      
      case 'auth_success':
        addConsoleMessage('✅ Authentication successful');
        break;

      case 'token_expired':
        // Token expirado, pedir uno nuevo
        addConsoleMessage('⚠️ Token expired - requesting new token...');
        refreshAuthToken();
        break;
        
      case 'user_message_received':
        const sourceInfo = data.source ? ` via ${data.source.toUpperCase()}` : '';
        addConsoleMessage(`📨 Processing${sourceInfo}: "${data.user_text}"`);
        break;
        
      case 'ai_response':
        const responseSourceInfo = data.source ? ` (${data.source.toUpperCase()})` : '';
        addConsoleMessage(`🤖 AI${responseSourceInfo}: ${data.message}`);
        break;
        
      case 'ai_audio':
        if (data.audio_data && data.text) {
          playAudioFromBase64(data.audio_data, data.text);
        }
        break;

      case 'ai_audio_streaming':
        if (data.audio_data && data.text) {
          handleStreamingAudio(data.audio_data, data.text, data.is_final || false);
        }
        break;

      case 'ai_response_complete':
        const completeSourceInfo = data.source ? ` (${data.source.toUpperCase()})` : '';
        addConsoleMessage(`🤖 Complete AI${completeSourceInfo}: ${data.message}`);
        
        if (audioQueue.current.length === 0 && !isPlayingStream) {
          setStreamingActive(false);
          setTimeout(() => {
            if (isListening && !isAISpeaking) {
              startRecognition();
              addConsoleMessage('🎤 Response complete - microphone reactivated');
            }
          }, 500);
        }
        break;
        
      case 'error':
        console.error('❌ Server error:', data.message);
        addConsoleMessage(`❌ Error: ${data.message}`);
        resetStreamingState();
        break;
        
      default:
        console.warn('Unknown message type received:', data.type);
        addConsoleMessage(`⚠️ Unknown message type: ${data.type}`);
        break;
    }
  }, [addConsoleMessage, playAudioFromBase64, handleStreamingAudio, isPlayingStream, isListening, isAISpeaking, startRecognition, resetStreamingState, context.webChatSettings?.ChatBotApiAccessToken, sendJsonMessage, refreshAuthToken]);

  useEffect(() => {
    console.log('Speech Recognition UseEffect triggered');
    
    if (recognition.current) {
      console.log('Speech Recognition UseEffect Rejected - already initialized');
      return; // Already initialized
    }

    console.log('Speech Recognition UseEffect - Initializing speech recognition...');

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      
      recognition.current.continuous = true;
      recognition.current.interimResults = false;
      recognition.current.lang = 'en-GB';
      recognition.current.maxAlternatives = 1;

      recognition.current.onresult = function(event: SpeechRecognitionEvent) {
        const lastResultIndex = event.results.length - 1;
        const result = event.results[lastResultIndex];

        if (result.isFinal) {
          const transcript = result[0].transcript.trim();
          
          if (transcript.length > 0) {
            const shouldDiscard = checkIfDiscarded(transcript);
            
            if (shouldDiscard) {
              return;
            }

            phraseCounter.current++;
        
            setTimeout(() => {
              lastAIText.current = "";
            }, 5000);
        
            const sent = sendTextToServer(transcript);
        
            if (!sent) {
              addConsoleMessage('❌ Error sending message');
            }
          }
        }
      };

      recognition.current.onerror = function(event: SpeechRecognitionErrorEvent) {
        console.error('Voice recognition error:', event.error);
        addConsoleMessage(`❌ Microphone error: ${event.error}`);
    
        if (event.error === 'not-allowed') {
          addConsoleMessage('❌ Microphone permission denied');
          setIsListening(false);
        } else if (event.error === 'no-speech') {
          addConsoleMessage('⚠️ No voice detected - continuing...');
        } else {
          addConsoleMessage('⚠️ Minor error - conversation continues...');
        }
      };                  

      recognition.current.onend = function() {
        if (isListening && !isAISpeaking && !streamingActive) {
          recognitionRestartTimeout.current = setTimeout(() => {
            if (isListening && !isAISpeaking && !streamingActive) {
              try {
                startRecognition();
                addConsoleMessage('🔄 Microphone restarted - ready for input');
              } catch (error) {
                console.error('Error restarting recognition:', error);
                addConsoleMessage('❌ Error restarting microphone');
              }
            }
          }, 200);
        }
      };

      recognition.current.onstart = function() {
        if (isListening && !isAISpeaking) {
          addConsoleMessage('🎤 Microphone is active - you can speak now');
        }
      };
      
      console.log('Speech recognition initialized successfully');
    } else {
      console.log('Speech Recognition API not supported in this browser');
      setTimeout(() => addConsoleMessage('❌ Speech Recognition API not supported in this browser'), 0);
      recognition.current = null;
    }

    return () => {
      console.log('Speech Recognition UseEffect unmounting');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('WebSocket lastMessage useEffect triggered');
    try {
      if (!lastMessage) return;

      const data = JSON.parse(lastMessage.data);
      handleServerMessage(data);

    } catch (error) {
        console.error('Error parsing server message:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]); // Empty dependency array - only run once to prevent re-initialization
  
  useEffect(() => {
      const connectedToWebSocket = (readyState === ReadyState.OPEN);
      setIsConnected(connectedToWebSocket);
      console.log(`WebSocket connection status changed: ${connectedToWebSocket ? 'Connected' : 'Disconnected'}`);
  }, [readyState]);



  // Función para iniciar conversación
  const startConversation = useCallback(async (): Promise<void> => {
    sessionId.current = crypto.randomUUID();
    addConsoleMessage(`🎤 Starting conversation session: ${sessionId.current}`);
    
    await ensureMicPermission();

    if (!recognition.current) {
      addConsoleMessage('❌ Voice recognition not available');
      return;
    }
    
    if (!isConnected) {
      addConsoleMessage('❌ No server connection. Connecting...');
      return;
    }
    
    try {
      setIsListening(true);
      setIsAISpeaking(false);
      phraseCounter.current = 0;
    
      startRecognition();
    
      addConsoleMessage('=== AI CONVERSATION STARTED ===');
    
    } catch (error) {
      console.error('Error starting voice recognition:', error);
      addConsoleMessage(`❌ Error starting voice recognition: ${(error as Error).message}`);
      setIsListening(false);
    }
  }, [ensureMicPermission, isConnected, addConsoleMessage, startRecognition]);

  // Función para detener conversación
  const stopConversation = useCallback((): void => {
    setIsListening(false);
    setIsAISpeaking(false);
    
    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current = null;
    }
    
    // Stop audio streaming
    audioQueue.current = [];
    setIsPlayingStream(false);
    setStreamingActive(false);
    
    if (aiSpeakingTimeout.current) {
      clearTimeout(aiSpeakingTimeout.current);
    }
    if (recognitionRestartTimeout.current) {
      clearTimeout(recognitionRestartTimeout.current);
    }
    
    stopRecognition();
    
    addConsoleMessage('=== AI CONVERSATION ENDED ===');
    addConsoleMessage(`Total exchanges: ${phraseCounter.current}`);
  }, [addConsoleMessage, stopRecognition]);

  return {
    // Estados
    isListening,
    isAISpeaking,
    isConnected,
    streamingActive,
    isPlayingStream,
    bufferSize,
    consoleMessages,
    audioQueueLength: audioQueue.current.length,
    connectionError: readyState === ReadyState.CLOSED || readyState === ReadyState.CLOSING,
    webChatSettings: context.webChatSettings,
    
    // Funciones
    startConversation,
    stopConversation,
    setBufferSize,
    
    // Estado computado
    status: (() => {
      if (!context.webChatSettings) {
        return { type: 'inactive', text: 'Voice chat settings not available' };
      } else if (streamingActive && (isPlayingStream || audioQueue.current.length > 0)) {
        return { type: 'streaming', text: `🚀 Streaming audio... (${audioQueue.current.length} in buffer)` };
      } else if (isAISpeaking) {
        return { type: 'ai-speaking', text: '🤖 AI is speaking... (microphone muted)' };
      } else if (isListening) {
        return { type: 'listening', text: '🎤 Listening... Your turn' };
      } else if (isConnected) {
        return { type: 'inactive', text: 'Connected - Press "Start Conversation" to speak' };
      } else if (websocketUrl) {
        return { type: 'inactive', text: 'Connecting to server...' };
      } else {
        return { type: 'inactive', text: 'Waiting for configuration...' };
      }
    })() as ChatStatus,
    
    micIconClass: (() => {
      if (streamingActive && (isPlayingStream || audioQueue.current.length > 0)) {
        return 'streaming';
      } else if (isAISpeaking) {
        return 'muted';
      } else if (isListening) {
        return 'active';
      } else {
        return 'inactive';
      }
    })() as 'streaming' | 'muted' | 'active' | 'inactive'
  };

};

export default useVoiceChatbot;